import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { CSSProperties } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import { useHelpDoc, useHelpNav } from "@/features/help/help";
import {
  useMySupportMessages,
  useSubmitSupportMessage,
} from "@/features/help/useSupport";
import { HelpNav } from "@/components/HelpNav";
import { ArticleSkeleton, RowListSkeleton } from "@/components/Skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

/** Help center. Without a slug this is the section index; with one it's the
 * article view plus the doc sidebar (ports /help and /help/[slug]). */
export default function HelpPage() {
  const { slug } = useParams();
  return slug ? <HelpArticle slug={slug} /> : <HelpIndex />;
}

function HelpIndex() {
  const { data } = useHelpNav();

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl">Help &amp; Guides</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        How to use the AFELL Directory. Press{" "}
        <kbd className="bg-muted rounded px-1.5 font-mono text-xs">⌘K</kbd> to
        search everything, including these articles.
      </p>

      {data === undefined ? (
        <div className="mt-8 space-y-8">
          <div>
            <Skeleton className="h-5 w-40" />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <RowListSkeleton count={4} withAvatar={false} />
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {data.sections.map((s, si) => (
            <div
              key={s.section}
              className="animate-item-enter"
              style={{ "--stagger": si } as CSSProperties}
            >
              <h2 className="text-lg">{s.section}</h2>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {s.articles.map((a) => (
                  <Link key={a.slug} to={`/help/${a.slug}`}>
                    <Card className="ease-out-quart hover:ring-foreground/20 h-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                          {a.title}
                          {a.audience === "admin" && (
                            <Badge variant="outline">Admin</Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{a.summary}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <ContactAdmins />
    </div>
  );
}
function HelpArticle({ slug }: { slug: string }) {
  const { data: doc, isPending, error } = useHelpDoc(slug);

  return (
    <div className="grid max-w-6xl gap-8 lg:grid-cols-[240px_1fr]">
      <aside className="hidden lg:block">
        <div className="sticky top-6">
          <Link
            to="/help"
            className="text-muted-foreground mb-4 block text-sm hover:underline"
          >
            ← Help &amp; Guides
          </Link>
          <HelpNav />
        </div>
      </aside>

      <div className="min-w-0">
        {isPending ? (
          <ArticleSkeleton />
        ) : error || !doc ? (
          <p className="text-muted-foreground mt-12 text-center">
            This article isn&apos;t available.
          </p>
        ) : (
          <article className="animate-page-enter">
            <p className="text-muted-foreground text-sm">{doc.section}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h1 className="text-3xl">{doc.title}</h1>
              {doc.audience === "admin" && (
                <Badge variant="outline">Admin</Badge>
              )}
            </div>

            <div className="prose prose-neutral dark:prose-invert mt-6 max-w-none">
              <ReactMarkdown>{doc.body}</ReactMarkdown>
            </div>

            <div className="mt-12 grid gap-3 border-t pt-6 sm:grid-cols-2">
              {doc.prev ? (
                <Link to={`/help/${doc.prev.slug}`}>
                  <Card className="hover:bg-accent/50 h-full transition-colors">
                    <CardHeader>
                      <p className="text-muted-foreground flex items-center gap-1 text-xs">
                        <ArrowLeft className="size-3" /> Previous
                      </p>
                      <p className="text-sm font-medium">{doc.prev.title}</p>
                    </CardHeader>
                  </Card>
                </Link>
              ) : (
                <div />
              )}
              {doc.next ? (
                <Link to={`/help/${doc.next.slug}`}>
                  <Card className="hover:bg-accent/50 h-full text-right transition-colors">
                    <CardHeader>
                      <p className="text-muted-foreground flex items-center justify-end gap-1 text-xs">
                        Next <ArrowRight className="size-3" />
                      </p>
                      <p className="text-sm font-medium">{doc.next.title}</p>
                    </CardHeader>
                  </Card>
                </Link>
              ) : (
                <div />
              )}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}

/**
 * Writing to the administrators.
 *
 * The answer arrives as an ordinary email, because that is where the
 * conversation will actually happen: the notification an admin receives
 * carries this member's address as its Reply-To, so their reply reaches the
 * member directly rather than a noreply mailbox nobody reads.
 */
function ContactAdmins() {
  const submit = useSubmitSupportMessage();
  const { data } = useMySupportMessages();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const sent = data?.messages ?? [];
  const ready = subject.trim() && body.trim();

  return (
    <Card className="mt-10">
      <CardHeader>
        <CardTitle className="text-base">
          <Mail className="mr-1.5 inline size-4" />
          Ask an administrator
        </CardTitle>
        <CardDescription>
          If the answer is not here, write to us. We will reply by email.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="support-subject">Subject</Label>
          <Input
            id="support-subject"
            value={subject}
            maxLength={200}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="support-body">Message</Label>
          <Textarea
            id="support-body"
            rows={5}
            value={body}
            maxLength={5000}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={!ready || submit.isPending}
            onClick={() =>
              submit.mutate(
                { subject: subject.trim(), body: body.trim() },
                {
                  onSuccess: () => {
                    setSubject("");
                    setBody("");
                    toast.success("Sent. We will reply by email.");
                  },
                  onError: (e) => toast.error(e.message),
                }
              )
            }
          >
            {submit.isPending && <Spinner data-slot="icon" />}
            Send
          </Button>
        </div>

        {sent.length > 0 && (
          <details className="text-muted-foreground pt-2 text-sm">
            <summary className="cursor-pointer">
              What you have asked ({sent.length})
            </summary>
            <ul className="mt-2 space-y-2">
              {sent.map((message) => (
                <li key={message.id} className="rounded-md border px-3 py-2">
                  <span className="text-foreground font-medium">
                    {message.subject}
                  </span>
                  <span className="ml-2 text-xs">
                    {new Date(message.created_at).toLocaleDateString()}
                    {message.status !== "new" ? ` · ${message.status}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
